import { APP_IDS, type AppId } from '@feedback-service/shared';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface AppSelectProps {
  label: string;
  value?: AppId;
  onChange: (value: AppId) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export function AppSelect({
  label,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  className,
}: AppSelectProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'zh-CN';

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor="app-select" className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>
      <Select
        value={value ?? ''}
        onValueChange={(val) => onChange(val as AppId)}
        disabled={disabled}
      >
        <SelectTrigger
          id="app-select"
          className={error ? 'border-destructive focus:ring-destructive' : ''}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'app-error' : undefined}
        >
          <SelectValue placeholder={t('form.appId.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {APP_IDS.map((appId) => (
            <SelectItem key={appId.value} value={appId.value}>
              {appId.label[currentLang] || appId.label.en}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <p id="app-error" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
